import { useEffect, useMemo, useState } from "react";
import { Box, Progress, Text, VStack } from "@chakra-ui/react";
import { getAnalysisTips, getRandomTipIndex } from "./analysisTips";
import { TIPS_INTERVAL } from "../../../Api/constants";

const STATUS_SCHEME = {
  cancelled: "orange",
  completed: "green",
  error: "red",
  running: "blue",
};

const getHelperText = (progress, t) => {
  if (progress.status === "completed") {
    return t(
      "reports.analysisCompletedHelper",
      "اكتمل التحليل بنجاح ويمكنك الآن مراجعة النتائج أو تصدير التقرير.",
    );
  }

  if (progress.status === "cancelled") {
    return t(
      "reports.analysisCancelledHelper",
      "تم إيقاف التحليل بناءً على طلبك، ويمكنك تشغيله مرة أخرى في أي وقت.",
    );
  }

  if (progress.status === "error") {
    return t(
      "reports.analysisErrorHelper",
      "تعذر إكمال التحليل. راجع الرسالة الظاهرة ثم أعد المحاولة إذا لزم.",
    );
  }

  if (progress.hasStartedRun) {
    return t(
      "reports.aiRunStartedProgress",
      "بدأ تنفيذ التحليل لدى الذكاء الاصطناعي. إيقافه الآن قد يمنع استكمال النتيجة لكنه لا يضمن عدم استهلاك أي موارد.",
    );
  }

  return t(
    "reports.aiRunNotStartedProgress",
    "لم يبدأ التنفيذ لدى الذكاء الاصطناعي بعد. إيقاف العملية الآن يوقفها مبكرًا قبل اكتمالها.",
  );
};

export default function AnalysisProgressNotice({ progress, t }) {
  const language = t("en") === "Arabic" ? "ar" : "en";
  const tips = useMemo(() => getAnalysisTips(t, language), [language, t]);
  const shouldShowTips = progress?.active && progress?.status === "running";
  const [tipIndex, setTipIndex] = useState(() =>
    getRandomTipIndex(tips.length),
  );
  useEffect(() => {
    if (!shouldShowTips || !tips.length) {
      return undefined;
    }
    setTipIndex((current) => getRandomTipIndex(tips.length, current));
    const timer = window.setInterval(() => {
      setTipIndex((current) => getRandomTipIndex(tips.length, current));
    }, TIPS_INTERVAL);

    return () => window.clearInterval(timer);
  }, [shouldShowTips, tips]);
  if (!progress?.active) {
    return null;
  }

  return (
    <Box
      flex="1"
      border="1px solid"
      borderColor="blue.100"
      bg="blue.50"
      borderRadius="md"
      px={3}
      py={2}
    >
      <VStack spacing={1} align="stretch">
        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
          {progress.title}
        </Text>
        <Text fontSize="xs" color="gray.600">
          {progress.description}
        </Text>
        <Progress
          value={progress.progress || 0}
          colorScheme={STATUS_SCHEME[progress.status] || "blue"}
          size="sm"
          borderRadius="full"
        />
        <Text fontSize="xs" color="gray.500">
          {getHelperText(progress, t)}
        </Text>
        {shouldShowTips && Boolean(tips[tipIndex]) && (
          <Box mt={1} p={2} bg="whiteAlpha.700" borderRadius="md">
            <Text fontSize="xs" fontWeight="semibold" color="blue.700">
              {t("reports.analysisTipsLabel", "نصائح سريعة لفهم التقرير")}
            </Text>
            <Text fontSize="xs" color="gray.700" mt={1}>
              {tips[tipIndex]}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
}
